import React from 'react';
import styled from 'styled-components';
import Popover from '../Popover/Popover';
import { ReactComponent as BoldIcon } from '../../assets/bold-format.svg';
import { ReactComponent as ItalicIcon } from '../../assets/italic-format.svg';
import { ReactComponent as LinkIcon } from '../../assets/link-format.svg';
import { ReactComponent as UnlinkIcon } from '../../assets/unlink-format.svg';
import { ReactComponent as ListBulletIcon } from '../../assets/ul-format.svg';
import { ReactComponent as ListOrderedIcon } from '../../assets/ol-format.svg';
import LinkMenu from './LinkMenu';

const ToolbarStrip = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  background: ${({ theme }) => theme.colors?.grey1 || '#f6f6f6'};
  padding: 2px 6px;
  border-radius: 4px 4px 0 0;
`;

const Separator = styled.span`
  width: 1px;
  height: 20px;
  background: ${({ theme }) => theme.colors?.grey2 || '#d3d3d3'};
  margin: 0 4px;
`;

const Button = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: none;
  background: transparent;
  border-radius: 50%;

  cursor: pointer;
  color: ${({ theme }) => theme.colors?.grey4 ?? '#696969'};

  &:hover:not(:disabled):not(.is-active) {
    opacity: 0.7;
  }

  &.is-active {
    background: ${({ theme }) => theme.colors?.grey2 || '#ebebeb'};
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
    color: ${({ theme }) => theme.colors.grey3};
  }

  svg {
    width: 20px;
    height: 20px;
    min-width: 20px;
    min-height: 20px;
    flex-shrink: 0;
    color: inherit;
  }
`;

const Toolbar = ({ editor, linkMenu, onOpenLinkMenu, onCloseLinkMenu, onLinkDone }) => {
  if (!editor) return null;

  const isEditable = editor.isEditable;

  return (
    <ToolbarStrip>
      <Button
        type="button"
        disabled={!isEditable}
        className={editor.isActive('bold') ? 'is-active' : ''}
        onClick={() => editor.chain().focus().toggleBold().run()}
        title="Bold">
        <BoldIcon />
      </Button>
      <Button
        type="button"
        disabled={!isEditable}
        className={editor.isActive('italic') ? 'is-active' : ''}
        onClick={() => editor.chain().focus().toggleItalic().run()}
        title="Italic">
        <ItalicIcon />
      </Button>
      <Separator />
      <Button
        type="button"
        disabled={!isEditable}
        className={editor.isActive('bulletList') ? 'is-active' : ''}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        title="Bullet list">
        <ListBulletIcon />
      </Button>
      <Button
        type="button"
        disabled={!isEditable}
        className={editor.isActive('orderedList') ? 'is-active' : ''}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        title="Numbered list">
        <ListOrderedIcon />
      </Button>
      <Separator />
      <Popover
        isPopoverOpen={!!linkMenu}
        onClickOutside={onCloseLinkMenu}
        clickOutsideCapture
        positions={['bottom', 'top', 'right', 'left']}
        showArrow={false}
        containerStyle={{ zIndex: 1000 }}
        popoverContent={
          linkMenu ? (
            <LinkMenu
              key={linkMenu.instanceKey}
              initialUrl={linkMenu.initialUrl}
              onDone={onLinkDone}
              onClose={onCloseLinkMenu}
            />
          ) : null
        }
        triggerContent={
          <Button
            type="button"
            disabled={!isEditable || (editor.state.selection.empty && !editor.isActive('link'))}
            className={editor.isActive('link') ? 'is-active' : ''}
            onMouseDown={(e) => {
              if (e.button !== 0) return;
              e.preventDefault();
              onOpenLinkMenu();
            }}
            onClick={(e) => {
              if (e.detail === 0) onOpenLinkMenu();
            }}
            title="Add link">
            <LinkIcon />
          </Button>
        }
      />
      <Button
        type="button"
        disabled={!isEditable || !editor.isActive('link')}
        onClick={() => editor.chain().focus().unsetLink().run()}
        title="Break link">
        <UnlinkIcon />
      </Button>
    </ToolbarStrip>
  );
};

export default Toolbar;
